// utils/securityLogger.js
class SecurityLogger {
  constructor() {
    this.logLevel = process.env.REACT_APP_LOG_LEVEL || 'warn';
    this.environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  }

  // Eventos críticos de seguridad
  logSecurityEvent(event, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'SECURITY_EVENT',
      event,
      details: {
        ...details,
        userAgent: navigator.userAgent,
        url: window.location.href,
        sessionId: this.getSessionId(),
      },
      level: 'CRITICAL',
    };

    this.sendLog(logEntry);
  }

  // Intentos de login fallidos
  logFailedLogin(email, reason) {
    this.logSecurityEvent('FAILED_LOGIN', {
      email: this.hashEmail(email),
      reason,
      ip: this.getClientIP(),
    });
  }

  // Login exitoso
  logSuccessfulLogin(userType) {
    this.logSecurityEvent('SUCCESSFUL_LOGIN', {
      userType,
    });
  }

  // Logout
  logLogout(reason = 'manual') {
    this.logSecurityEvent('LOGOUT', {
      reason, // manual, timeout, forced
    });
  }

  // Sesión expirada
  logSessionExpired() {
    this.logSecurityEvent('SESSION_EXPIRED', {});
  }

  // Actividad sospechosa
  logSuspiciousActivity(activityType, details) {
    this.logSecurityEvent('SUSPICIOUS_ACTIVITY', {
      activityType,
      ...details,
    });
  }

  // Rate limiting activado
  logRateLimitHit(attempts) {
    this.logSecurityEvent('RATE_LIMIT_HIT', {
      attempts,
    });
  }

  // Errores de autenticación
  logAuthError(error, context) {
    this.logSecurityEvent('AUTH_ERROR', {
      error: error.message,
      context,
    });
  }

  // Métodos de utilidad
  getSessionId() {
    return localStorage.getItem('sessionId') || 'unknown';
  }

  hashEmail(email) {
    // Solo loggear hash para privacidad
    if (!email) return 'unknown';
    return btoa(email).substring(0, 8) + '***';
  }

  getClientIP() {
    // En producción, esto debe venir del servidor
    return 'client-side';
  }

  sendLog(logEntry) {
    // Desarrollo: console
    if (this.environment === 'development') {
      console.warn('🔒 Security Log:', logEntry);
    }

    // Producción: enviar a servicio de logging
    if (this.environment === 'production') {
      this.sendToLoggingService(logEntry);
    }

    // Siempre guardar en localStorage para debug (limitar tamaño)
    this.saveToLocalStorage(logEntry);
  }

  sendToLoggingService(logEntry) {
    // Integración con Sentry, LogRocket, etc.
    try {
      if (window.Sentry) {
        window.Sentry.captureMessage('Security Event', {
          level: 'warning',
          extra: logEntry,
        });
      }

      // O enviar a tu propio endpoint de logging
      fetch('/api/security-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry),
      }).catch(err => {
        console.error('Failed to send security log:', err);
      });
    } catch (error) {
      console.error('Logging service error:', error);
    }
  }

  saveToLocalStorage(logEntry) {
    try {
      const logs = JSON.parse(localStorage.getItem('securityLogs') || '[]');
      logs.push(logEntry);

      // Mantener solo los últimos 50 logs
      if (logs.length > 50) {
        logs.splice(0, logs.length - 50);
      }

      localStorage.setItem('securityLogs', JSON.stringify(logs));
    } catch (error) {
      // Si localStorage está lleno, limpiar logs antiguos
      localStorage.removeItem('securityLogs');
    }
  }

  // Método para obtener logs para debugging
  getSecurityLogs() {
    try {
      return JSON.parse(localStorage.getItem('securityLogs') || '[]');
    } catch {
      return [];
    }
  }

  // Limpiar logs (para GDPR compliance)
  clearLogs() {
    localStorage.removeItem('securityLogs');
  }
}

// Singleton instance
const securityLogger = new SecurityLogger();

export default securityLogger;

// Métodos de conveniencia
export const logFailedLogin = (email, reason) => securityLogger.logFailedLogin(email, reason);
export const logSuccessfulLogin = userType => securityLogger.logSuccessfulLogin(userType);
export const logLogout = reason => securityLogger.logLogout(reason);
export const logSessionExpired = () => securityLogger.logSessionExpired();
export const logSuspiciousActivity = (type, details) =>
  securityLogger.logSuspiciousActivity(type, details);
export const logRateLimitHit = attempts => securityLogger.logRateLimitHit(attempts);
export const logAuthError = (error, context) => securityLogger.logAuthError(error, context);
